// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { LogManager } from "@lib/logging/LogManager";
import { NetworkType } from "@lib/messaging/NetworkType";
import { RawMessage } from "@lib/messaging/RawMessage";
import { RawMessageSource } from "@lib/messaging/RawMessageSource";
import { SocketEndpoint } from "@lib/messaging/SocketEndpoint";
import { Assert } from "@lib/utilities/Assert";
import { EventListener } from "@lib/utilities/EventListener";
import { IEventListener } from "@lib/utilities/IEventListener";

type RouteInfo<TNetworkType extends NetworkType> = {
  [K in Exclude<NetworkType, TNetworkType>]: SocketEndpoint;
};

const log = LogManager.createLog("NetworkNode");

/**
 * Routes incoming messages according to a routing table.
 * Messages addressed to the current node are propagated using an event.
 */
export class NetworkNode
{
  static create<TNetworkType extends NetworkType>(
    networkType: TNetworkType,
    routes: RouteInfo<TNetworkType>,
    doRouting: boolean): NetworkNode
  {
    return new NetworkNode(networkType, routes, doRouting);
  }

  private readonly _onMessage = new EventListener<(source: RawMessageSource, rawMessage: RawMessage) => void>();
  private readonly routingTable: Dictionary<SocketEndpoint> = {};
  readonly doRouting: boolean;
  readonly networkType: NetworkType;

  private constructor(networkType: NetworkType, routes: { [key: string]: SocketEndpoint }, doRouting: boolean)
  {
    this.networkType = networkType;
    this.doRouting = doRouting;

    const endpoints = new Map<SocketEndpoint, Set<string>>();
    for (const route in routes)
    {
      if (!routes.hasOwnProperty(route))
        continue;

      const endpoint = routes[route];

      if (route === this.networkType)
        throw new Error("Route can not have the same network type.");

      if (route in this.routingTable)
        throw new Error(`Route for network node '${route}' is already registered`);

      let allowedOrigins = endpoints.get(endpoint);
      if (!allowedOrigins)
      {
        allowedOrigins = new Set();
        endpoints.set(endpoint, allowedOrigins);
      }

      allowedOrigins.add(route);
      this.routingTable[route] = endpoint;
    }

    for (const [endpoint, allowedOrigins] of endpoints)
    {
      if (endpoint.socketType === "single")
      {
        endpoint.onMessage.addListener((endpoint, socket, rawMessage) => this.handleMessage({
          endpoint: endpoint,
          node: this,
          socket: socket
        }, rawMessage, allowedOrigins));
      }
      else if (endpoint.socketType === "multi")
      {
        endpoint.onMessage.addListener((endpoint, socket, socketID, rawMessage) => this.handleMessage({
          endpoint: endpoint,
          node: this,
          socket: socket,
          socketID: socketID
        }, rawMessage, allowedOrigins));
      }
      else
      {
        Assert.ensureNever(endpoint);
      }
    }
  }

  get onMessage(): IEventListener<(source: RawMessageSource, rawMessage: RawMessage) => void>
  {
    return this._onMessage;
  }

  sendRawMessage(rawMessage: RawMessage): void
  {
    this.sendRawMessageInternal(rawMessage, true);
  }

  private handleMessage(source: RawMessageSource, rawMessage: RawMessage, allowedOrigins: Set<string>): void
  {
    if (!allowedOrigins.has(rawMessage.source.networkType))
    {
      log.error("Dropped received message: invalid source (%o)", rawMessage);
      return;
    }

    if (!rawMessage.destination.equals(this.networkType))
    {
      if (!this.doRouting)
      {
        log.error("Dropped received message: no-routing policy (%o)", rawMessage);
        return;
      }

      this.sendRawMessageInternal(rawMessage, false);
      log.trace("Routed message: '%s' -->| '%s' (%o)", rawMessage.source.toString(), rawMessage.destination.toString(), rawMessage);
      return;
    }

    this._onMessage.dispatch(source, rawMessage);
  }

  private sendRawMessageInternal(rawMessage: RawMessage, checkSource: boolean)
  {
    if (checkSource && rawMessage.source.networkType !== this.networkType)
    {
      log.error("Can not send raw message: invalid source (%o)", rawMessage);
      return;
    }

    const endpoint = this.routingTable[rawMessage.destination.networkType];
    if (!endpoint)
    {
      log.error("Can not send raw message: could not find endpoint for destination (%o)", rawMessage);
      return;
    }

    if (endpoint.socketType === "single")
    {
      endpoint.sendRawMessage(rawMessage);
    }
    else if (endpoint.socketType === "multi")
    {
      const id = rawMessage.destination.networkIdentifier;
      if (!id)
      {
        log.error("Could not send raw message: required version not specified (%o)", rawMessage);
        return;
      }

      endpoint.sendRawMessage(id, rawMessage);
    }
    else
    {
      Assert.ensureNever(endpoint);
      return;
    }
  }
}